'use client';

import cn from 'classnames';
import { DayPicker } from 'react-day-picker';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { DEFAULT_LOCALE } from '@/shared/config';
import { MONTHS } from '@/shared/constants/calendar';
import { getYearsRange } from '../lib/helpers';
import { useCalendar } from '../lib/use-calendar';
import { CalendarProps } from '../model/calendar.props';
import s from './calendar.module.scss';


export function Calendar ({
    selected,
    onSelect,
    onClear,
    showClear = true,
    className,
    ...props
}: CalendarProps) {
    const {
        handlePrev, handleTitleClick, title, handleNext, view, month, setMonth, isMonthDisabled, handleMonthPick,
        isYearDisabled, handleYearPick, handleClear,
    } = useCalendar({ selected, onSelect, onClear, disabled: props.disabled });

    return (
        <div className={ cn(s.calendar, className) }>
            <div className={ s.header }>
                <button
                    type="button"
                    className={ s.nav }
                    onClick={ handlePrev }
                    aria-label="Previous"
                >
                    <CaretLeftIcon/>
                </button>

                <button
                    type="button"
                    className={ s.title }
                    onClick={ handleTitleClick }
                    aria-label="Switch view"
                >
                    { title }
                </button>

                <button
                    type="button"
                    className={ s.nav }
                    onClick={ handleNext }
                    aria-label="Next"
                >
                    <CaretRightIcon/>
                </button>
            </div>

            <div className={ s.body }>
                { view === 'days' && (
                    <DayPicker
                        mode="single"
                        selected={ selected }
                        onSelect={ onSelect }
                        month={ month }
                        onMonthChange={ setMonth }
                        showOutsideDays
                        weekStartsOn={ 0 }
                        classNames={ {
                            months       : s.months,
                            month        : s.month,
                            month_caption: s.monthCaption,
                            caption_label: s.captionLabel,
                            nav          : s.dpNav,
                            weekdays     : s.weekdays,
                            weekday      : s.weekday,
                            week         : s.week,
                            day          : s.day,
                            day_button   : s.dayButton,
                            selected     : s.selected,
                            today        : s.today,
                            outside      : s.outside,
                            disabled     : s.disabled,
                            hidden       : s.hidden,
                        } }
                        { ...props }
                    />
                )
                }
                {
                    view === 'months' &&
                    <div className={ s.monthsGrid }>
                        {
                            MONTHS[DEFAULT_LOCALE].full.nominative.map((name, idx) => {
                                const disabled = isMonthDisabled(idx);

                                return (
                                    <button
                                        key={ name }
                                        type="button"
                                        disabled={ disabled }
                                        className={ cn(s.monthCell, {
                                            [s.monthCellSelected]: idx === month.getMonth(),
                                            [s.cellDisabled]     : disabled,
                                        }) }
                                        onClick={ () => handleMonthPick(idx) }
                                    >
                                        { name }
                                    </button>
                                );
                            })
                        }
                    </div>
                }

                {
                    view === 'years' &&
                    <div className={ s.yearsGrid }>
                        {
                            getYearsRange(month.getFullYear()).map((year) => {
                                const disabled = isYearDisabled(year);

                                return (
                                    <button
                                        key={ year }
                                        type="button"
                                        disabled={ disabled }
                                        className={ cn(s.yearCell, {
                                            [s.yearCellSelected]: year === month.getFullYear(),
                                            [s.cellDisabled]    : disabled,
                                        }) }
                                        onClick={ () => handleYearPick(year) }
                                    >
                                        { year }
                                    </button>
                                );
                            })
                        }
                    </div>
                }
            </div>

            {
                showClear &&
                <button
                    type="button"
                    className={ s.clear }
                    onClick={ handleClear }
                >
                    Clear
                </button>
            }
        </div>
    );
}
